import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE } from '../config/apiConfig';
import CIDTooltip from './CIDTooltip';

/**
 * LLMTop10 — LLM-driven industry-standard BI questions per product_type.
 *
 * Instead of hardcoded questions, the LLM generates the top 10 most valuable
 * business intelligence questions based on the client's product type and
 * current market conditions.
 *
 * RECYCLING LOGIC:
 *   When a question is consumed (added to Custom Intelligence, shared, or
 *   deep-dived), it is added to a persistent "used questions" exclusion list
 *   stored in localStorage per client. On every fetch/refresh, the exclusion
 *   list is sent to the backend so the LLM generates completely NEW questions
 *   that don't repeat previously consumed ones.
 *
 * Each question row includes:
 *   ➕  Add to Custom Intelligence  (removes from Top-10, adds to exclusion)
 *   📤  Share (with consent)         (removes from Top-10, adds to exclusion)
 *   🔗  Take me to separate LLM session (deep-dive with consent)
 */

// ── Helpers: localStorage-backed exclusion list ──
const STORAGE_KEY_PREFIX = 'pnp_top10_used_';

function getUsedQuestions(clientId) {
  try {
    return JSON.parse(localStorage.getItem(`${STORAGE_KEY_PREFIX}${clientId}`) || '[]');
  } catch { return []; }
}

function addUsedQuestion(clientId, question) {
  const used = getUsedQuestions(clientId);
  const normalized = question.trim().toLowerCase();
  if (!used.some(q => q.trim().toLowerCase() === normalized)) {
    used.push(question.trim());
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${clientId}`, JSON.stringify(used));
  }
  return used;
}

// ── Action type badge colors ──
const ACTION_COLORS = {
  'Recommend':       { bg: '#e8f5e9', color: '#2e7d32', label: 'Recommend' },
  'Execute':         { bg: '#e3f2fd', color: '#1565c0', label: 'Execute' },
  'Monitor Outcome': { bg: '#fff3e0', color: '#e65100', label: 'Monitor' },
  'Decision To Be Made': { bg: '#fce4ec', color: '#880e4f', label: 'Decision' },
};

// ── Icon button base style ──
const iconBtnStyle = {
  background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
  fontSize: '14px', opacity: 0.6, transition: 'opacity 0.15s',
  lineHeight: 1, display: 'inline-flex', alignItems: 'center',
};

/**
 * Render markdown content with executive-presentable formatting.
 * Handles: tables, **bold**, *italic*, ## headers, bullet lists, numbered lists, --- dividers, code blocks.
 * Produces styled output suitable for executive presentations and Word/PDF export.
 */
function renderMarkdownWithTables(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const blocks = [];
  let currentText = [];
  let tableLines = [];
  let inTable = false;

  const flushText = () => {
    if (currentText.length > 0) {
      blocks.push({ type: 'text', content: currentText.join('\n') });
      currentText = [];
    }
  };

  const flushTable = () => {
    if (tableLines.length >= 2) {
      const parseRow = (line) => line.split('|').map(c => c.trim()).filter(c => c.length > 0);
      const headers = parseRow(tableLines[0]);
      const rows = tableLines.slice(1)
        .filter(l => !l.match(/^\|[\s\-:|]+\|$/))
        .map(parseRow);
      blocks.push({ type: 'table', headers, rows });
    } else {
      currentText.push(...tableLines);
    }
    tableLines = [];
    inTable = false;
  };

  for (const line of lines) {
    const isTableLine = line.trim().startsWith('|') && line.trim().endsWith('|') && line.includes('|');
    if (isTableLine) {
      if (!inTable) { flushText(); inTable = true; }
      tableLines.push(line);
    } else {
      if (inTable) { flushTable(); }
      currentText.push(line);
    }
  }
  if (inTable) flushTable();
  flushText();

  // Render inline markdown formatting
  const renderInline = (str) => {
    if (!str) return str;
    // Bold
    let result = str.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Italic
    result = result.replace(/\*(.+?)\*/g, '<em>$1</em>');
    // Inline code
    result = result.replace(/`([^`]+)`/g, '<code style="background:#f0f0f0;padding:1px 4px;border-radius:3px;font-size:11px">$1</code>');
    return result;
  };

  return blocks.map((block, i) => {
    if (block.type === 'table') {
      return (
        <div key={i} style={{ margin: '12px 0', overflowX: 'auto', borderRadius: '8px', border: '1px solid #e0e0e0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#1a237e', color: '#fff' }}>
                {block.headers.map((h, j) => (
                  <th key={j} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: '11px', whiteSpace: 'nowrap' }}>
                    {h.replace(/\*\*/g, '')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} style={{ background: ri % 2 === 0 ? '#f8f9fa' : '#fff', borderBottom: '1px solid #f0f0f0' }}>
                  {row.map((cell, ci) => {
                    let cellStyle = { padding: '7px 12px', fontSize: '12px', color: '#333' };
                    const cellLower = cell.toLowerCase();
                    if (cellLower.includes('high')) cellStyle.color = '#c62828';
                    else if (cellLower.includes('moderate')) cellStyle.color = '#e65100';
                    else if (cellLower.includes('low')) cellStyle.color = '#2e7d32';
                    if (ci === 0) cellStyle.fontWeight = 600;
                    return <td key={ci} style={cellStyle}>{cell.replace(/\*\*/g, '')}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Render text block with markdown formatting
    const textLines = block.content.split('\n');
    return (
      <span key={i}>
        {textLines.map((line, li) => {
          const trimmed = line.trim();

          // Horizontal rule
          if (trimmed.match(/^---+$/) || trimmed.match(/^\*\*\*+$/)) {
            return <hr key={li} style={{ border: 'none', borderTop: '1px solid #e0e0e0', margin: '12px 0' }} />;
          }

          // H2 header (## )
          if (trimmed.startsWith('## ')) {
            return <div key={li} style={{ fontSize: '15px', fontWeight: 700, color: '#1a237e', margin: '16px 0 6px', borderBottom: '1px solid #e0e0e0', paddingBottom: '4px' }} dangerouslySetInnerHTML={{ __html: renderInline(trimmed.slice(3)) }} />;
          }
          // H3 header (### )
          if (trimmed.startsWith('### ')) {
            return <div key={li} style={{ fontSize: '13px', fontWeight: 700, color: '#283593', margin: '12px 0 4px' }} dangerouslySetInnerHTML={{ __html: renderInline(trimmed.slice(4)) }} />;
          }
          // H4+ header (#### )
          if (trimmed.startsWith('####')) {
            const text = trimmed.replace(/^#+\s*/, '');
            return <div key={li} style={{ fontSize: '12px', fontWeight: 700, color: '#333', margin: '8px 0 3px' }} dangerouslySetInnerHTML={{ __html: renderInline(text) }} />;
          }
          // H1 header (# )
          if (trimmed.startsWith('# ') && !trimmed.startsWith('## ')) {
            return <div key={li} style={{ fontSize: '17px', fontWeight: 700, color: '#1a237e', margin: '18px 0 8px', borderBottom: '2px solid #1a237e', paddingBottom: '4px' }} dangerouslySetInnerHTML={{ __html: renderInline(trimmed.slice(2)) }} />;
          }

          // Numbered list item (1. , 2. , etc.)
          if (trimmed.match(/^\d+\.\s+/)) {
            const content = trimmed.replace(/^\d+\.\s+/, '');
            return <div key={li} style={{ padding: '3px 0 3px 16px', fontSize: '12px', display: 'flex', gap: '6px' }}><span style={{ color: '#1a237e', fontWeight: 700, minWidth: '16px' }}>{trimmed.match(/^\d+/)[0]}.</span><span dangerouslySetInnerHTML={{ __html: renderInline(content) }} /></div>;
          }

          // Bullet point (- or • )
          if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
            const content = trimmed.slice(2);
            const indent = line.length - line.trimStart().length;
            return <div key={li} style={{ padding: '2px 0 2px ' + (16 + indent * 8) + 'px', fontSize: '12px', display: 'flex', gap: '6px' }}><span style={{ color: '#1565c0' }}>•</span><span dangerouslySetInnerHTML={{ __html: renderInline(content) }} /></div>;
          }

          // Empty line
          if (trimmed === '') {
            return <div key={li} style={{ height: '6px' }} />;
          }

          // Regular paragraph
          return <div key={li} style={{ fontSize: '12px', lineHeight: 1.7, margin: '2px 0' }} dangerouslySetInnerHTML={{ __html: renderInline(trimmed) }} />;
        })}
      </span>
    );
  });
}

/**
 * Deep-Dive Session Modal — shows in-depth LLM analysis for a question.
 */
const DeepDiveModal = ({ question, actionType, clientId, productType, llmProvider, onClose }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // ── PDF filename dialog state ──
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printFilename, setPrintFilename] = useState('');

  // ── 6e Expansion state ──
  const [expansion6e, setExpansion6e] = useState(null);
  const [expansion6eLoading, setExpansion6eLoading] = useState(false);
  const [expansion6eError, setExpansion6eError] = useState(null);
  const expansion6eRef = useRef(null);
  const expansion6eDataRef = useRef(null); // Always holds latest 6e data for PDF/copy
  const reportContentRef = useRef(null);

  // Keep ref in sync with state so executePrint (hoisted function) always reads latest
  useEffect(() => { expansion6eDataRef.current = expansion6e; }, [expansion6e]);

  // ── PDF filename helpers (localStorage-backed for cross-session versioning) ──
  const USED_FILENAMES_KEY = 'pnp_used_pdf_filenames';

  const getUsedFilenames = () => {
    try { return JSON.parse(localStorage.getItem(USED_FILENAMES_KEY) || '[]'); }
    catch { return []; }
  };

  const markFilenameUsed = (name) => {
    const used = getUsedFilenames();
    const key = name.toLowerCase().trim();
    if (!used.includes(key)) {
      used.push(key);
      localStorage.setItem(USED_FILENAMES_KEY, JSON.stringify(used));
    }
  };

  const buildBaseFilename = (question, productType) => {
    const base = `Business Atomic Intelligence Report — ${productType || ''}`;
    if (!question) return base;
    const stopWords = new Set(['what','is','our','are','the','a','an','and','or','of',
      'in','on','at','to','for','do','does','how','which','that','with','we','by',
      'most','can','be','has','have','been','should','would','will','from','this','it']);
    const words = question
      .replace(/[^a-zA-Z0-9\s-]/g, ' ')
      .split(/\s+/)
      .map(w => w.toLowerCase())
      .filter(w => w.length > 2 && !stopWords.has(w));
    const slug = words.slice(0, 4)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join('-');
    return slug ? `${base}-${slug}` : base;
  };

  const getVersionedFilename = (base) => {
    const used = getUsedFilenames();
    if (!used.includes(base.toLowerCase().trim())) return base;
    let v = 2;
    while (used.includes(`${base}-v${v}`.toLowerCase().trim())) v++;
    return `${base}-v${v}`;
  };

  const openPrintDialog = () => {
    const base = buildBaseFilename(question, productType);
    const versioned = getVersionedFilename(base);
    setPrintFilename(versioned);
    setShowPrintDialog(true);
  };

  // Get business name, URL, and address from session for branded report heading
  const businessName = (() => {
    try {
      const session = JSON.parse(localStorage.getItem('pnp_subscriber_session') || '{}');
      return session.company_name || session.business_name || '';
    } catch { return ''; }
  })();
  const [businessSubline, setBusinessSubline] = useState(() => {
    try {
      const session = JSON.parse(localStorage.getItem('pnp_subscriber_session') || '{}');
      // URL takes precedence over address
      if (session.business_url || session.website || session.website_url) {
        return session.business_url || session.website || session.website_url;
      }
      if (session.business_address || session.location) {
        return session.business_address || session.location;
      }
      // Fallback: check onboarded clients store for this clientId
      const allClients = JSON.parse(localStorage.getItem('pnp_onboarded_clients') || '[]');
      const thisClient = allClients.find(c => c.client_id === clientId);
      if (thisClient) {
        return thisClient.website_url || thisClient.website || thisClient.business_address || thisClient.location || '';
      }
      return '';
    } catch { return ''; }
  });

  // If businessSubline is empty, fetch from View Client API
  useEffect(() => {
    if (businessSubline || !clientId) return;
    const fetchClientInfo = async () => {
      try {
        const res = await fetch(`${API_BASE}/clients/${clientId}/status`);
        if (res.ok) {
          const data = await res.json();
          const sub = data.website_url || data.website || data.business_address || data.location || '';
          if (sub) {
            setBusinessSubline(sub);
            // Also persist to session for next time
            try {
              const session = JSON.parse(localStorage.getItem('pnp_subscriber_session') || '{}');
              if (data.website_url || data.website) session.website_url = data.website_url || data.website;
              if (data.location || data.business_address) session.business_address = data.business_address || data.location;
              localStorage.setItem('pnp_subscriber_session', JSON.stringify(session));
            } catch { /* ignore */ }
          }
        }
      } catch { /* ignore */ }
    };
    fetchClientInfo();
  }, [clientId, businessSubline]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    const fetchDeepDive = async () => {
      try {
        const res = await fetch(`${API_BASE}/clients/${clientId}/deep-dive-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            question,
            action_type: actionType,
            product_type: productType,
            llm_provider: llmProvider || 'gemini',
          }),
        });
        if (!cancelled) {
          if (res.ok) {
            const data = await res.json();
            setAnalysis(data);
            // Auto-save to Custom Intelligence (persist the BUC question)
            try {
              const questionText = typeof question === 'string' ? question : question?.question || '';
              if (questionText) {
                await fetch(`${API_BASE}/clients/${clientId}/custom-intelligence`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    question: questionText,
                    label: questionText.slice(0, 60),
                    instruction: '',
                    category: 'deep_dive',
                    source: 'ddls_auto_save',
                  }),
                });
              }
            } catch (e) { /* non-critical — question still shown in session */ }
          } else {
            setError('Failed to start deep-dive session. Please try again.');
          }
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError('Unable to connect to the analysis service.');
          setLoading(false);
        }
      }
    };
    fetchDeepDive();
    return () => { cancelled = true; };
  }, [question, actionType, clientId, productType, llmProvider]);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {/* Print styles — force backgrounds, colors, and compact spacing for PDF */}
      <style>{`
        @media print {
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
          body > *:not(.ddls-print-root) { display: none !important; }
          .ddls-print-root { position: static !important; background: #fff !important; overflow: visible !important; }
          .ddls-print-root > div { position: static !important; width: 100% !important; max-width: 100% !important; height: auto !important; max-height: none !important; overflow: visible !important; border-radius: 0 !important; box-shadow: none !important; }
          .ddls-report-content { overflow: visible !important; max-height: none !important; }
          .ddls-report-content table { page-break-inside: avoid; border-collapse: collapse !important; width: 100% !important; }
          .ddls-report-content thead { display: table-header-group !important; }
          .ddls-report-content thead tr { page-break-after: avoid !important; }
          .ddls-report-content tbody { display: table-row-group !important; }
          .ddls-report-content tbody tr:first-child { page-break-before: avoid !important; }
          .ddls-report-content tr { page-break-inside: avoid !important; }
          .ddls-report-content th, .ddls-report-content td { padding: 6px 10px !important; margin: 0 !important; border: 1px solid #ccc !important; }
          .ddls-report-content th { background: #1a237e !important; color: #fff !important; }
          .ddls-report-content h1, .ddls-report-content h2, .ddls-report-content h3 { page-break-after: avoid !important; margin-top: 12px !important; margin-bottom: 4px !important; }
          .ddls-report-content h1 + *, .ddls-report-content h2 + *, .ddls-report-content h3 + * { page-break-before: avoid !important; }
          .ddls-report-content ul, .ddls-report-content ol { margin-top: 4px !important; margin-bottom: 4px !important; }
          .ddls-report-content li { margin-bottom: 2px !important; }
          .ddls-report-content p { margin-top: 4px !important; margin-bottom: 4px !important; orphans: 3; widows: 3; }
          .ddls-no-print { display: none !important; }
        }
      `}</style>
      <div className="ddls-print-root" style={{
        background: '#fff', borderRadius: expanded ? '0' : '12px',
        width: expanded ? '100%' : '90%', maxWidth: expanded ? '100%' : '720px',
        height: expanded ? '100%' : 'auto', maxHeight: expanded ? '100%' : '85vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: expanded ? 'none' : '0 20px 60px rgba(0,0,0,0.3)',
        transition: 'all 0.2s ease',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid #e0e0e0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#f8f9fa',
        }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
              🔍 Deep-Dive LLM Session {llmProvider ? `(${llmProvider.charAt(0).toUpperCase() + llmProvider.slice(1)})` : '(Gemini)'}
            </div>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
              In-depth analysis with reasons, solutions &amp; actionable recommendations
            </div>
          </div>
          <div className="ddls-no-print" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={() => setExpanded(!expanded)} style={{
              background: 'none', border: '1px solid #ddd', borderRadius: '4px',
              cursor: 'pointer', color: '#666', padding: '4px 8px', fontSize: '12px',
            }} title={expanded ? 'Collapse' : 'Expand fullscreen'}>
              {expanded ? '⊟' : '⊞'}
            </button>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', fontSize: '20px',
              cursor: 'pointer', color: '#999', padding: '4px 8px',
            }} aria-label="Close deep-dive session">✕</button>
          </div>
        </div>

        {/* Question banner */}
        <div style={{
          padding: '12px 20px', background: '#e8f5e9', borderBottom: '1px solid #c8e6c9',
          fontSize: '12px', color: '#2e7d32',
        }}>
          <strong>Question:</strong> {question}
        </div>

        {/* Content */}
        <div className="ddls-report-content" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
              <div style={{ fontSize: '24px', marginBottom: '12px' }}>🧠</div>
              <div>Analyzing your question in depth...</div>
              <div style={{ fontSize: '11px', marginTop: '8px', color: '#aaa' }}>
                This may take a few seconds as the LLM generates a comprehensive analysis.
              </div>
            </div>
          )}
          {error && (
            <div style={{ padding: '16px', background: '#ffebee', borderRadius: '8px', color: '#c62828', fontSize: '13px' }}>
              ⚠️ {error}
            </div>
          )}
          {analysis && (
            <div>
              <div style={{ fontSize: '10px', color: '#aaa', marginBottom: '12px', display: 'flex', gap: '12px' }}>
                <span>Model: {analysis.model}</span>
                <span>Session: {analysis.session_id}</span>
                {analysis.latency_ms && <span>{Math.round(analysis.latency_ms)}ms</span>}
              </div>
              {analysis.sections && analysis.sections.length > 0 ? (
                analysis.sections.map((section, i) => (
                  <div key={i} style={{
                    marginBottom: '16px', padding: '12px 16px',
                    background: section.title.toLowerCase().includes('technical') || section.title.toLowerCase().includes('sql') || section.title.toLowerCase().includes('nlp')
                      ? '#e8eaf6' : (i % 2 === 0 ? '#fafafa' : '#fff'),
                    borderRadius: '8px',
                    border: section.title.toLowerCase().includes('technical') || section.title.toLowerCase().includes('sql')
                      ? '1.5px solid #7986cb' : '1px solid #f0f0f0',
                  }}>
                    <div style={{
                      fontSize: '13px', fontWeight: 600, marginBottom: '8px',
                      borderBottom: '1px solid #e0e0e0', paddingBottom: '6px',
                      color: section.title.toLowerCase().includes('technical') || section.title.toLowerCase().includes('sql') || section.title.toLowerCase().includes('nlp')
                        ? '#283593' : '#333',
                    }}>
                      {section.title.toLowerCase().includes('technical') ? '🔧 ' : ''}
                      {section.title.toLowerCase().includes('sql') ? '💾 ' : ''}
                      {section.title.toLowerCase().includes('nlp') ? '🧠 ' : ''}
                      {section.title.toLowerCase().includes('table') ? '📋 ' : ''}
                      {section.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#555', lineHeight: 1.7 }}>
                      {renderMarkdownWithTables(section.content)}
                    </div>
                    {section.action_items && section.action_items.length > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#1565c0', marginBottom: '4px' }}>Action Items:</div>
                        {section.action_items.map((item, j) => (
                          <div key={j} style={{ fontSize: '11px', color: '#333', padding: '3px 0 3px 12px', borderLeft: '2px solid #1565c0', marginBottom: '2px' }}>
                            {item}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ fontSize: '12px', color: '#555', lineHeight: 1.7 }}>
                  {renderMarkdownWithTables(analysis.analysis)}
                </div>
              )}

              {/* Technical Context — Tables from backend */}
              {analysis.technical && analysis.technical.tables && analysis.technical.tables.length > 0 && (
                <div style={{
                  marginTop: '12px', padding: '12px 16px', background: '#e3f2fd',
                  borderRadius: '8px', border: '1.5px solid #64b5f6',
                }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#0d47a1', marginBottom: '8px' }}>
                    📋 Database Tables ({analysis.technical.db_source}) — {analysis.technical.table_count} tables
                  </div>
                  <div style={{ fontSize: '11px', color: '#333', fontFamily: 'monospace', lineHeight: 1.6 }}>
                    {analysis.technical.tables.map((t, i) => (
                      <div key={i} style={{ marginBottom: '4px', padding: '4px 8px', background: i % 2 === 0 ? '#e8f5e9' : '#fff', borderRadius: '4px' }}>
                        <strong>{t.table}</strong>: <span style={{ color: '#555' }}>{t.columns}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '10px', color: '#666', marginTop: '6px' }}>
                    {analysis.technical.has_live_db ? '✅ Live database connected' : '📋 Demo tables — connect your database for live data'}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 6e Loading state */}
          {expansion6eLoading && (
            <div style={{ marginTop: '12px', textAlign: 'center', padding: '16px', color: '#888', fontSize: '11px' }}>
              📊 Generating Back-Testing & Forward-Testing...
            </div>
          )}

          {/* 6e Error state */}
          {expansion6eError && (
            <div style={{ marginTop: '8px', padding: '8px', background: '#ffebee', borderRadius: '4px', fontSize: '11px', color: '#c62828' }}>
              ⚠️ {expansion6eError}
            </div>
          )}

          {/* 6e Rendered content */}
          {expansion6e && (
            <div ref={expansion6eRef} style={{
              marginTop: '16px', padding: '12px 16px', background: '#e8eaf6',
              borderRadius: '8px', border: '1.5px solid #7986cb',
            }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#283593', marginBottom: '8px', borderBottom: '1px solid #e0e0e0', paddingBottom: '6px' }}>
                📊 6e. Back-Testing & Forward-Testing
                <span style={{ fontSize: '10px', color: '#888', fontWeight: 400, marginLeft: '8px' }}>
                  {expansion6e.model} · {Math.round(expansion6e.latency_ms || 0)}ms
                </span>
              </div>
              <div style={{ fontSize: '12px', color: '#555', lineHeight: 1.7 }}>
                {renderMarkdownWithTables(expansion6e.content)}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'flex-end', gap: '8px', background: '#f8f9fa' }}>
          {analysis && (
            <button
              onClick={() => {
                // Build branded clipboard content with acronym expansion
                const acronyms = {
                  'CID': "CEO's Intelligence Directives (CID)",
                  'DDLS': 'Deep Dive Learning Session (DDLS)',
                  'NLP': 'Natural Language Processing (NLP)',
                  'SQL': 'Structured Query Language (SQL)',
                  'KPI': 'Key Performance Indicator (KPI)',
                  'RLS': 'Row-Level Security (RLS)',
                  'PNP': 'Plug-N-Play (PNP)',
                  'BI': 'Business Intelligence (BI)',
                };
                let content = (analysis.analysis || '') + (expansion6eDataRef.current ? '\n\n' + expansion6eDataRef.current.content : '');
                // Expand acronyms (first occurrence only)
                const expanded = new Set();
                Object.entries(acronyms).forEach(([acr, full]) => {
                  if (!expanded.has(acr)) {
                    const regex = new RegExp(`\\b${acr}\\b`);
                    if (regex.test(content)) {
                      content = content.replace(regex, full);
                      expanded.add(acr);
                    }
                  }
                });
                // Convert markdown-style formatting to Word-compatible heading hierarchy
                // Strip # markdown headers and --- dividers — not valid in Word/PDF
                let htmlContent = content
                  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                  // Strip markdown horizontal rules
                  .replace(/^---+$/gm, '')
                  .replace(/^\*\*\*+$/gm, '')
                  // Convert # H1 → bold large heading (no literal #)
                  .replace(/^#{1}\s+(.+)$/gm, '<div style="font-size:18px;font-weight:bold;color:#4A148C;margin:16px 0 6px 0;border-bottom:2px solid #4A148C;padding-bottom:4px">$1</div>')
                  // Convert ## H2 → bold medium heading
                  .replace(/^#{2}\s+(.+)$/gm, '<div style="font-size:15px;font-weight:bold;color:#1A237E;margin:14px 0 4px 0">$1</div>')
                  // Convert ### H3 → bold small heading
                  .replace(/^#{3}\s+(.+)$/gm, '<div style="font-size:13px;font-weight:bold;color:#283593;margin:10px 0 3px 0">$1</div>')
                  // Convert #### H4 → bold body
                  .replace(/^#{4,}\s+(.+)$/gm, '<div style="font-size:12px;font-weight:bold;color:#333;margin:8px 0 2px 0">$1</div>')
                  // Bold inline
                  .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
                  // Italic inline
                  .replace(/\*(.+?)\*/g, '<i>$1</i>')
                  // Bullet points
                  .replace(/^[-•]\s+(.+)$/gm, '<div style="padding-left:20px;margin:2px 0">• $1</div>')
                  .replace(/^\d+\.\s+(.+)$/gm, '<div style="padding-left:20px;margin:2px 0">$1</div>')
                  // Colored callouts
                  .replace(/⚠️(.*?)(?=\n|$)/g, '<span style="color:#D32F2F">⚠️$1</span>')
                  .replace(/✅(.*?)(?=\n|$)/g, '<span style="color:#2E7D32">✅$1</span>')
                  // Code blocks
                  .replace(/```sql([\s\S]*?)```/g, '<pre style="background:#f5f5f5;padding:10px;font-family:Consolas,monospace;font-size:11px;border:1px solid #ddd;white-space:pre-wrap">$1</pre>')
                  .replace(/```([\s\S]*?)```/g, '<pre style="background:#f5f5f5;padding:10px;font-family:Consolas,monospace;font-size:11px;border:1px solid #ddd;white-space:pre-wrap">$1</pre>')
                  // Newlines to breaks (after block-level replacements)
                  .replace(/\n/g, '<br/>');
                const htmlBranded = `
<div style="font-family:Calibri,Arial,sans-serif;max-width:800px;margin:0 auto;padding:20px">
  <div style="text-align:center;padding:20px 0 16px 0;border-bottom:3px solid #4A148C;margin-bottom:16px">
    ${businessName ? `<div style="font-size:28px;font-weight:bold;color:#1a237e;margin-bottom:4px;letter-spacing:0.5px">${businessName}</div>` : ''}
    ${businessSubline ? (businessSubline.startsWith('http') ? `<div style="font-size:22px;color:#333;margin-bottom:6px"><a href="${businessSubline}" style="color:#1565C0;text-decoration:none">${businessSubline}</a></div>` : `<div style="font-size:22px;color:#333;margin-bottom:6px">${businessSubline}</div>`) : ''}
    <div style="font-size:22px;font-weight:bold;color:#4A148C">Business Atomic Intelligence${productType ? ' — ' + productType : ''}</div>
    <div style="font-size:13px;font-weight:bold;color:#4A148C;margin-top:4px">Recommendations — Execution — Monitor Outcome — Decisions to be Made</div>
    <div style="font-size:12px;color:#555;margin-top:6px">Brought To You by <a href="https://Plug-N-Play-Infra.ai" style="color:#4A148C;font-weight:bold">https://Plug-N-Play-Infra.ai</a> (PNP)</div>
  </div>
  <div style="margin-bottom:16px">
    <div style="font-size:17px;font-weight:bold;color:#000;margin-bottom:6px">Question: &ldquo;${question.replace(/"/g, '&quot;')}&rdquo;</div>
    <div style="font-size:11px;color:#888">Model: ${analysis.model} &nbsp;|&nbsp; Session: ${analysis.session_id} &nbsp;|&nbsp; ${Math.round(analysis.latency_ms || 0)}ms &nbsp;|&nbsp; Generated: ${new Date().toISOString().slice(0, 16).replace('T', ' ')} UTC</div>
  </div>
  <div style="font-size:13px;line-height:1.8;color:#222">
    ${htmlContent}
  </div>
  <div style="border-top:2px solid #4A148C;padding:14px 0 0 0;text-align:center;margin-top:24px">
    <div style="font-size:11px;font-weight:bold;color:#4A148C">Business Atomic Intelligence${productType ? ' — ' + productType : ''} — Plug-N-Play-Infra.ai</div>
    <div style="font-size:10px;color:#888;margin-top:2px">Generated: ${new Date().toISOString().slice(0, 16).replace('T', ' ')} UTC &nbsp;|&nbsp; Confidential</div>
  </div>
</div>`;
                // Plain text fallback — strip all markdown symbols
                const plainText = [
                  '═══════════════════════════════════════════════════════',
                  ...(businessName ? [businessName] : []),
                  ...(businessSubline ? [businessSubline] : []),
                  `Business Atomic Intelligence${productType ? ' — ' + productType : ''}`,
                  'Recommendations — Execution — Monitor Outcome — Decisions to be Made',
                  'Brought To You by https://Plug-N-Play-Infra.ai (PNP)',
                  '═══════════════════════════════════════════════════════',
                  '',
                  `Question: "${question}"`,
                  `Model: ${analysis.model} | Session: ${analysis.session_id} | ${Math.round(analysis.latency_ms || 0)}ms | Generated: ${new Date().toISOString().slice(0, 16).replace('T', ' ')} UTC`,
                  '───────────────────────────────────────────────────────',
                  '',
                  content
                    .replace(/^#{1,6}\s+/gm, '')   // strip # headers
                    .replace(/^---+$/gm, '')         // strip --- dividers
                    .replace(/\*\*(.+?)\*\*/g, '$1') // strip **bold**
                    .replace(/\*(.+?)\*/g, '$1'),    // strip *italic*
                  '',
                  '───────────────────────────────────────────────────────',
                  `Business Atomic Intelligence${productType ? ' — ' + productType : ''} — Plug-N-Play-Infra.ai`,
                  `Generated: ${new Date().toISOString().slice(0, 16).replace('T', ' ')} UTC | Confidential`,
                ].join('\n');
                // Copy as rich HTML (for Word/Docs) with plain text fallback
                const blob = new Blob([htmlBranded], { type: 'text/html' });
                const textBlob = new Blob([plainText], { type: 'text/plain' });
                navigator.clipboard.write([
                  new ClipboardItem({ 'text/html': blob, 'text/plain': textBlob })
                ]).then(() => {
                  const btn = document.getElementById('ddls-copy-btn');
                  if (btn) { btn.textContent = '✅ Copied!'; setTimeout(() => { btn.textContent = '📋 Copy Report'; }, 2000); }
                }).catch(() => {
                  // Fallback for browsers that don't support ClipboardItem
                  navigator.clipboard.writeText(plainText).then(() => {
                    const btn = document.getElementById('ddls-copy-btn');
                    if (btn) { btn.textContent = '✅ Copied!'; setTimeout(() => { btn.textContent = '📋 Copy Report'; }, 2000); }
                  });
                });
              }}
              id="ddls-copy-btn"
              className="ddls-no-print"
              style={{
                padding: '8px 20px', borderRadius: '6px', border: '1px solid #1565c0',
                background: '#1565c0', color: '#fff', cursor: 'pointer', fontSize: '12px',
                fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
              }}
              aria-label="Copy deep-dive analysis to clipboard"
            >
              📋 Copy Report
            </button>
          )}
          {analysis && (
            <button
              onClick={openPrintDialog}
              style={{
                padding: '8px 20px', borderRadius: '6px', border: '1px solid #2e7d32',
                background: '#2e7d32', color: '#fff', cursor: 'pointer', fontSize: '12px',
                fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
              }}
              aria-label="Print report as PDF"
            >
              🖨️ Print Report
            </button>
          )}
          {/* Generate Back-Testing button — visible only when not yet generated */}
          {analysis && analysis.expansion_available && !expansion6e && (
            <button
              onClick={async () => {
                setExpansion6eLoading(true);
                setExpansion6eError(null);
                try {
                  const res = await fetch(`${API_BASE}/clients/${clientId}/deep-dive-session/${analysis.session_id}/expand`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ section: '6e_backtesting', llm_provider: llmProvider || 'gemini' }),
                  });
                  if (res.ok) {
                    const data = await res.json();
                    setExpansion6e(data);
                    // Auto-scroll to 6e content after render
                    setTimeout(() => {
                      if (expansion6eRef.current) {
                        expansion6eRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 200);
                  } else {
                    setExpansion6eError('Failed to generate — try again');
                  }
                } catch {
                  setExpansion6eError('Unable to connect');
                }
                setExpansion6eLoading(false);
              }}
              disabled={expansion6eLoading}
              style={{
                padding: '8px 16px', borderRadius: '6px', border: '1px solid #E65100',
                background: expansion6eLoading ? '#ccc' : '#E65100', color: '#fff',
                cursor: expansion6eLoading ? 'wait' : 'pointer', fontSize: '11px',
                fontWeight: 600, whiteSpace: 'nowrap',
              }}
            >
              {expansion6eLoading ? '⏳ Generating...' : '📊 Generate Back-Testing'}
            </button>
          )}
          <button onClick={onClose} style={{
            padding: '8px 20px', borderRadius: '6px', border: '1px solid #ddd',
            background: '#fff', cursor: 'pointer', fontSize: '12px',
          }}>Close Session</button>
        </div>
      </div>

      {/* ── PDF Filename Dialog ── */}
      {showPrintDialog && analysis && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.55)', zIndex: 10001,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: '10px', padding: '28px 32px',
            width: '560px', maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#1a237e', marginBottom: '6px' }}>
              🖨️ Save Report as PDF
            </div>
            <div style={{ fontSize: '11px', color: '#888', marginBottom: '16px' }}>
              Edit the filename below. When the print dialog opens, choose "Save as PDF" and use this name.
              {(() => {
                const used = getUsedFilenames();
                const base = buildBaseFilename(question, productType);
                return used.includes(base.toLowerCase().trim())
                  ? <span style={{ color: '#e65100', marginLeft: '4px' }}>⚠ A file with a similar name was saved before — version suffix added.</span>
                  : null;
              })()}
            </div>
            <div style={{ marginBottom: '8px' }}>
              <label style={{ fontSize: '11px', fontWeight: 600, color: '#555', display: 'block', marginBottom: '4px' }}>
                File name:
              </label>
              <input
                autoFocus
                value={printFilename}
                onChange={e => setPrintFilename(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const finalName = printFilename.trim() || buildBaseFilename(question, productType);
                    executePrint(finalName);
                  }
                  if (e.key === 'Escape') setShowPrintDialog(false);
                }}
                style={{
                  width: '100%', padding: '8px 10px', fontSize: '12px',
                  border: '1.5px solid #1a237e', borderRadius: '6px',
                  fontFamily: 'Calibri, Arial, sans-serif', boxSizing: 'border-box',
                  color: '#222',
                }}
                placeholder="Enter filename..."
              />
              <div style={{ fontSize: '10px', color: '#aaa', marginTop: '4px' }}>
                .pdf extension will be added by your browser automatically
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => setShowPrintDialog(false)}
                style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: '12px' }}
              >Cancel</button>
              <button
                onClick={() => {
                  const finalName = printFilename.trim() || buildBaseFilename(question, productType);
                  executePrint(finalName);
                }}
                style={{ padding: '8px 24px', borderRadius: '6px', border: '1px solid #2e7d32', background: '#2e7d32', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
              >🖨️ Print / Save PDF</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── Execute print with the confirmed filename ──
  function executePrint(pdfTitle) {
    setShowPrintDialog(false);
    markFilenameUsed(pdfTitle);

    // Include 6e expansion content if it was generated
    const exp6e = expansion6eDataRef.current;
    const raw = (analysis.analysis || '') + (exp6e ? '\n\n' + exp6e.content : '');
    let content = raw
      .replace(/<span[^>]*color\s*:\s*red[^>]*>([\s\S]*?)<\/span>/gi,
        (_, inner) => `⚠WARN⚠${inner.replace(/<[^>]+>/g, '')}⚠/WARN⚠`)
      .replace(/<span[^>]*color\s*:\s*green[^>]*>([\s\S]*?)<\/span>/gi,
        (_, inner) => `✅MIT✅${inner.replace(/<[^>]+>/g, '')}✅/MIT✅`)
      .replace(/<[^>]+>/g, '');

    const processTable = (tableBlock) => {
      const rows = tableBlock.trim().split('\n');
      let thead = '', tbody = '', isFirst = true;
      for (const row of rows) {
        const trimmed = row.trim();
        if (!trimmed.startsWith('|')) continue;
        if (/^\|[\s\-:|]+\|$/.test(trimmed)) continue;
        const cells = trimmed.split('|').map(c => c.trim()).filter(c => c !== '');
        if (cells.length === 0) continue;
        if (isFirst) {
          thead = '<thead><tr style="-webkit-print-color-adjust:exact;print-color-adjust:exact;background:#1a237e!important;color:#fff!important">'
            + cells.map(c => `<th style="padding:7px 10px;font-size:12px;font-weight:600;background:#1a237e;color:#fff;border:1px solid #3949ab">${c.replace(/\*\*/g,'')}</th>`).join('')
            + '</tr></thead>';
          isFirst = false;
        } else {
          const rowCells = cells.map((c, ci) => {
            let cellStyle = 'padding:6px 10px;font-size:12px;border:1px solid #e0e0e0';
            if (ci === cells.length - 1) {
              const cl = c.toLowerCase();
              if (cl.includes('high')) cellStyle += ';color:#c62828;font-weight:700';
              else if (cl.includes('moderate')) cellStyle += ';color:#e65100;font-weight:700';
              else if (cl.includes('low')) cellStyle += ';color:#2e7d32;font-weight:700';
            }
            if (ci === 0) cellStyle += ';font-weight:600';
            return `<td style="${cellStyle}">${c.replace(/\*\*/g,'')}</td>`;
          });
          tbody += `<tr style="background:${tbody.split('<tr').length % 2 === 1 ? '#f8f9fa' : '#ffffff'}">${rowCells.join('')}</tr>`;
        }
      }
      return `<table style="border-collapse:collapse;width:100%;margin:10px 0;font-size:12px"><${thead}<tbody>${tbody}</tbody></table>`;
    };

    const lines = content.split('\n');
    const blocks = [];
    let currentBlock = [];
    let inTable = false;
    for (const line of lines) {
      const isTableLine = line.trim().startsWith('|') && line.trim().endsWith('|');
      if (isTableLine && !inTable) {
        if (currentBlock.length) { blocks.push({ type: 'text', content: currentBlock.join('\n') }); currentBlock = []; }
        inTable = true; currentBlock = [line];
      } else if (isTableLine && inTable) {
        currentBlock.push(line);
      } else if (!isTableLine && inTable) {
        blocks.push({ type: 'table', content: currentBlock.join('\n') }); currentBlock = [line]; inTable = false;
      } else {
        currentBlock.push(line);
      }
    }
    if (currentBlock.length) blocks.push({ type: inTable ? 'table' : 'text', content: currentBlock.join('\n') });

    const htmlContent = blocks.map(block => {
      if (block.type === 'table') return processTable(block.content);
      return block.content
        .replace(/^---+$/gm, '').replace(/^\*\*\*+$/gm, '')
        .replace(/^#{1}\s+(.+)$/gm, '<h1 style="font-size:18px;color:#1a237e;border-bottom:2px solid #1a237e;padding-bottom:4px;margin:20px 0 8px">$1</h1>')
        .replace(/^#{2}\s+(.+)$/gm, '<h2 style="font-size:15px;color:#283593;margin:16px 0 6px">$1</h2>')
        .replace(/^#{3}\s+(.+)$/gm, '<h3 style="font-size:13px;color:#555;margin:12px 0 4px">$1</h3>')
        .replace(/^#{4,}\s+(.+)$/gm, '<h4 style="font-size:12px;color:#777;margin:8px 0 3px;font-style:italic">$1</h4>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/⚠WARN⚠([\s\S]*?)⚠\/WARN⚠/g, '<div style="color:#c62828;font-weight:600;padding:4px 8px;border-left:3px solid #c62828;margin:4px 0;background:#fff8f8">$1</div>')
        .replace(/✅MIT✅([\s\S]*?)✅\/MIT✅/g, '<div style="color:#2e7d32;font-weight:600;padding:4px 8px;border-left:3px solid #2e7d32;margin:4px 0;background:#f8fff8">$1</div>')
        .replace(/^[-•]\s+(.+)$/gm, '<li style="margin:2px 0">$1</li>')
        .replace(/(<li.*?<\/li>\n?)+/g, '<ul style="padding-left:20px;margin:4px 0">$&</ul>')
        .replace(/```sql([\s\S]*?)```/g, '<pre style="background:#f5f5f5;padding:12px;font-family:Consolas,monospace;font-size:11px;border:1px solid #ddd;border-radius:4px;overflow-x:auto;white-space:pre-wrap">$1</pre>')
        .replace(/```([\s\S]*?)```/g, '<pre style="background:#f5f5f5;padding:12px;font-family:Consolas,monospace;font-size:11px;border:1px solid #ddd;border-radius:4px;white-space:pre-wrap">$1</pre>')
        .replace(/\n/g, '<br/>');
    }).join('');

    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>${pdfTitle}</title>
<style>
  *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}
  body{font-family:Calibri,Arial,sans-serif;padding:30px 40px;color:#222;font-size:13px;line-height:1.7;max-width:900px;margin:0 auto}
  h1{color:#1a237e;border-bottom:2px solid #1a237e;padding-bottom:4px}
  h2{color:#283593}h3{color:#555}
  table{border-collapse:collapse;width:100%;margin:10px 0;page-break-inside:avoid}
  thead{display:table-header-group}
  thead tr{background:#1a237e!important;color:#fff!important;-webkit-print-color-adjust:exact}
  thead th{background:#1a237e!important;color:#fff!important;padding:7px 10px;font-size:12px;font-weight:600;border:1px solid #3949ab}
  td{padding:6px 10px;border:1px solid #e0e0e0;font-size:12px}
  tr:nth-child(even) td{background:#f8f9fa}
  pre{background:#f5f5f5;padding:12px;border:1px solid #ddd;border-radius:4px;font-size:11px;overflow-x:auto;white-space:pre-wrap;page-break-inside:avoid}
  .header{text-align:center;border-bottom:3px solid #1a237e;padding-bottom:16px;margin-bottom:20px}
  .footer{border-top:2px solid #1a237e;padding-top:12px;margin-top:30px;text-align:center;font-size:10px;color:#888}
  @media print{
    *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
    body{padding:10px 20px}
    h1{font-size:16px}h2{font-size:14px}
    thead th{background:#1a237e!important;color:#fff!important}
    table{page-break-inside:avoid}
    tr{page-break-inside:avoid}
  }
</style></head><body>
<div class="header">
  ${businessName ? `<div style="font-size:26px;font-weight:bold;color:#1a237e;margin-bottom:4px">${businessName}</div>` : ''}
  ${businessSubline ? (businessSubline.startsWith('http') ? `<div style="font-size:14px;color:#1565C0;margin-bottom:6px"><a href="${businessSubline}" style="color:#1565C0">${businessSubline}</a></div>` : `<div style="font-size:14px;color:#333;margin-bottom:6px">${businessSubline}</div>`) : ''}
  <div style="font-size:20px;font-weight:bold;color:#1a237e">Business Atomic Intelligence${productType ? ' \u2014 ' + productType : ''}</div>
  <div style="font-size:12px;font-weight:600;color:#1a237e;margin-top:4px">Recommendations \u2014 Execution \u2014 Monitor Outcome \u2014 Decisions to be Made</div>
  <div style="font-size:11px;color:#555;margin-top:6px">Brought To You by https://Plug-N-Play-Infra.ai (PNP)</div>
</div>
<div style="margin-bottom:16px">
  <div style="font-size:15px;font-weight:bold;color:#000">Question: "${question.replace(/"/g, '&quot;')}"</div>
  <div style="font-size:10px;color:#888;margin-top:4px">Model: ${analysis.model} | Session: ${analysis.session_id} | ${Math.round(analysis.latency_ms || 0)}ms | Generated: ${new Date().toISOString().slice(0,16).replace('T',' ')} UTC</div>
</div>
<div>${htmlContent}</div>
<div class="footer">
  <div style="font-weight:600;color:#1a237e">Business Atomic Intelligence${productType ? ' \u2014 ' + productType : ''} \u2014 Plug-N-Play-Infra.ai</div>
  <div>Generated: ${new Date().toISOString().slice(0,16).replace('T',' ')} UTC | Confidential</div>
</div>
</body></html>`);
    win.document.close();
    setTimeout(() => { win.print(); }, 600);
  }
};

/**
 * Consent Modal — shown before starting a deep-dive session or sharing.
 */
const ConsentModal = ({ type, question, onConfirm, onCancel }) => {
  const isDeepDive = type === 'deep-dive';
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.4)', zIndex: 9998,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: '10px', padding: '24px',
        width: '90%', maxWidth: '440px', boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
      }}>
        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px', color: '#333' }}>
          {isDeepDive ? '🔍 Start Deep-Dive LLM Session?' : '📤 Share this question?'}
        </div>
        <div style={{
          fontSize: '12px', color: '#555', lineHeight: 1.6, marginBottom: '8px',
          padding: '10px 12px', background: '#f8f9fa', borderRadius: '6px',
          borderLeft: '3px solid #1565c0',
        }}>
          &ldquo;{question}&rdquo;
        </div>
        <div style={{ fontSize: '11px', color: '#888', marginBottom: '16px', lineHeight: 1.5 }}>
          {isDeepDive
            ? 'This will open a separate LLM session that provides in-depth analysis, root causes, solutions, and actionable recommendations for this business question. Your question will be sent to the AI for detailed processing.'
            : 'This will share the question with other users in your organization. They will be able to see and use this question in their own BI panel. The question will be removed from your Top 10 and replaced with a new one on refresh.'
          }
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onCancel} style={{
            padding: '8px 16px', borderRadius: '6px', border: '1px solid #ddd',
            background: '#fff', cursor: 'pointer', fontSize: '12px', color: '#666',
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            padding: '8px 16px', borderRadius: '6px', border: 'none',
            background: isDeepDive ? '#1565c0' : '#4CAF50', color: '#fff',
            cursor: 'pointer', fontSize: '12px', fontWeight: 600,
          }}>{isDeepDive ? '🔍 Start Session' : '📤 Share'}</button>
        </div>
      </div>
    </div>
  );
};

/**
 * LLMTop10Panel — Renders the Top-10 as clickable buttons with action icons.
 *
 * RECYCLING BEHAVIOR:
 *   - usedQuestions (localStorage) tracks every question consumed via ➕ or 📤
 *   - On fetch/refresh, usedQuestions are sent as exclude_questions to the backend
 *   - The LLM prompt includes the exclusion list so it generates NEW questions
 *   - When ➕ or 📤 is clicked, the question is immediately removed from the
 *     visible list (optimistic removal) and added to the exclusion list
 *   - "Clear history" button lets the user reset the exclusion list
 *
 * Props:
 *   productType      — e.g. "Restaurant.Type-1"
 *   clientId         — the client identifier
 *   llmProviderId    — user's configured LLM provider (e.g. "gemini", "claude", "chatgpt")
 *   onRunQuestion(q) — callback when user clicks a question to run it
 *   onAddToCustom(q) — callback when user clicks + to add to Custom Intelligence
 *   onShare(q)       — callback when user shares a question
 */
const VISIBLE_COUNT_KEY = 'pnp_top10_visible_';
const DEFAULT_VISIBLE = 3;
const MAX_VISIBLE = 8;
const ROW_HEIGHT = 38; // approx height per question row in px

const LLMTop10Panel = ({ productType, clientId, llmProviderId, mainLLMProvider, onRunQuestion, onAddToCustom, onShare }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modelUsed, setModelUsed] = useState('');

  // ── Blotter provider: always Gemini for BUCI/Top-10 questions ──
  // Do NOT change this to alternate with ChatGPT — ChatGPT times out on cold start.
  // See known-bugs-and-lessons.md for full context.
  const blotterProvider = "gemini";
  // ── Configurable visible count: default 3, max 8, persisted per client ──
  const [visibleCount, setVisibleCount] = useState(() => {
    const saved = localStorage.getItem(`${VISIBLE_COUNT_KEY}${clientId}`);
    return saved ? Math.min(parseInt(saved, 10) || DEFAULT_VISIBLE, MAX_VISIBLE) : DEFAULT_VISIBLE;
  });
  const [showCountConfig, setShowCountConfig] = useState(false);

  // ── Exclusion list: persisted per client in localStorage ──
  const [usedQuestions, setUsedQuestions] = useState(() => getUsedQuestions(clientId));

  // Consent state
  const [consentType, setConsentType] = useState(null);
  const [consentQuestion, setConsentQuestion] = useState(null);
  const [consentActionType, setConsentActionType] = useState(null);

  // Deep-dive session state
  const [deepDiveQuestion, setDeepDiveQuestion] = useState(null);
  const [deepDiveActionType, setDeepDiveActionType] = useState(null);

  // CID Tooltip state for blotter header
  const [cidTooltipVisible, setCidTooltipVisible] = useState(false);
  const top10HeaderRef = useRef(null);

  // ── Mark a question as consumed ──
  const consumeQuestion = useCallback((questionText) => {
    const updated = addUsedQuestion(clientId, questionText);
    setUsedQuestions(updated);
    setQuestions(prev => prev.filter(q => q.q.trim().toLowerCase() !== questionText.trim().toLowerCase()));
  }, [clientId]);

  // ── Fetch LLM-generated questions (always Gemini — fastest, no timeout risk) ──
  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    setError(null);
    const currentUsed = getUsedQuestions(clientId);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout
    try {
      const res = await fetch(`${API_BASE}/clients/${clientId}/top10-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_type: productType,
          exclude_questions: currentUsed,
          llm_provider: 'gemini', // Always Gemini — fastest for BUCI/Top-10
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        const data = await res.json();
        const usedLower = new Set(currentUsed.map(q => q.trim().toLowerCase()));
        const filtered = (data.questions || []).filter(q => !usedLower.has(q.q.trim().toLowerCase()));
        setQuestions(filtered);
        setModelUsed(data.model || '');
        setError(null);
      } else {
        setError('Could not load questions — provider unavailable');
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        setError('Request timed out — click 🔄 to retry');
      } else {
        setError('Unable to connect to the server');
      }
      // Retain previous questions on error (don't clear them)
    }
    setLoading(false);
  }, [clientId, productType]); // blotterProvider removed — it's always "gemini"

  useEffect(() => {
    if (clientId && clientId !== 'unknown' && productType) fetchQuestions();
  }, [clientId, productType, fetchQuestions]);

  // ── Clear exclusion history ──
  const clearHistory = () => {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${clientId}`);
    setUsedQuestions([]);
    fetchQuestions();
  };

  // ── Visible count change ──
  const handleVisibleCountChange = (count) => {
    setVisibleCount(count);
    localStorage.setItem(`${VISIBLE_COUNT_KEY}${clientId}`, count);
    setShowCountConfig(false);
  };

  // ── Consent handlers ──
  const handleDeepDiveClick = (item) => {
    setConsentType('deep-dive');
    setConsentQuestion(item.q);
    setConsentActionType(item.action_type);
  };

  const handleShareClick = (item) => {
    setConsentType('share');
    setConsentQuestion(item.q);
    setConsentActionType(item.action_type);
  };

  const handleConsentConfirm = () => {
    if (consentType === 'deep-dive') {
      setDeepDiveQuestion(consentQuestion);
      setDeepDiveActionType(consentActionType);
    } else if (consentType === 'share') {
      consumeQuestion(consentQuestion);
      if (onShare) onShare(consentQuestion);
    }
    setConsentType(null);
    setConsentQuestion(null);
    setConsentActionType(null);
  };

  const handleConsentCancel = () => {
    setConsentType(null);
    setConsentQuestion(null);
    setConsentActionType(null);
  };

  const handleAddToCustom = (item) => {
    consumeQuestion(item.q);
    if (onAddToCustom) onAddToCustom(item.q);
  };

  // Compute scrollable height based on visible count
  const scrollHeight = visibleCount * ROW_HEIGHT;

  return (
    <div>
      {/* Header — purple ellipse matching Smart Decisions Dashboard style */}
      <div
        ref={top10HeaderRef}
        onMouseEnter={() => setCidTooltipVisible(true)}
        onMouseLeave={() => setCidTooltipVisible(false)}
        style={{
        fontSize: '11px', color: '#555', marginBottom: '4px', padding: '2px 4px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: '#F3E5F5', border: '1.5px solid #CE93D8', borderRadius: '20px',
      }}>
        <span style={{ padding: '2px 8px' }}>
          <strong style={{ color: '#7B1FA2', fontSize: '11px' }}>
            🧠 AI Top-10 {(() => {
              const pt = (productType || '').toLowerCase();
              if (pt.includes('china-us') || pt.includes('strategic-intel')) return 'Geopolitical Intelligence';
              if (pt.includes('procurement')) return 'Procurement Intelligence';
              if (pt.includes('bank') || pt.includes('financial')) return 'Banking Risk';
              if (pt.includes('manufacturing') || pt.includes('predictivemaintenance')) return 'Predictive Maintenance';
              if (pt.includes('restaurant') || pt.includes('hospitality')) return 'Restaurant Operations';
              if (pt.includes('gym') || pt.includes('fitness')) return 'Fitness Operations';
              if (pt.includes('grocery') || pt.includes('supermarket')) return 'Grocery Operations';
              if (pt.includes('insurance')) return 'Insurance Operations';
              if (pt.includes('realty') || pt.includes('realestate')) return 'Real Estate';
              if (pt.includes('logistics')) return 'Logistics';
              if (pt.includes('medical') || pt.includes('wellness')) return 'Medical';
              if (pt.includes('hvac')) return 'HVAC Service';
              if (pt.includes('plumbing')) return 'Plumbing Network';
              if (pt.includes('lawn')) return 'Lawn & Landscape';
              if (pt.includes('valet') || pt.includes('auto')) return 'Valet Operations';
              return 'BI';
            })()} Questions for {productType || 'your business'}
          </strong>
          {modelUsed && <span style={{ color: '#9575CD', fontWeight: 400, fontSize: '10px' }}> · {modelUsed}</span>}
          <span style={{ color: '#1565c0', fontWeight: 600, fontSize: '10px', marginLeft: '4px', background: '#e3f2fd', padding: '1px 6px', borderRadius: '8px' }}>
            {blotterProvider.charAt(0).toUpperCase() + blotterProvider.slice(1)}
          </span>
          {usedQuestions.length > 0 && (
            <span style={{ color: '#AB47BC', fontWeight: 400, fontSize: '10px', marginLeft: '4px' }}>
              · {usedQuestions.length} recycled
            </span>
          )}
        </span>
        <span style={{ display: 'inline-flex', gap: '3px', alignItems: 'center', position: 'relative' }}>
          {/* ⚙️ Visible count config */}
          <button
            onClick={() => setShowCountConfig(!showCountConfig)}
            style={{
              background: 'none', border: '1px solid #e0e0e0', borderRadius: '4px',
              padding: '1px 6px', fontSize: '10px', cursor: 'pointer', color: '#888',
            }}
            title={`Showing ${visibleCount} questions (click to change, max ${MAX_VISIBLE})`}
            aria-label="Configure visible question count"
          >
            ⚙️ {visibleCount}
          </button>
          {showCountConfig && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, zIndex: 120,
              background: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)', padding: '6px 8px',
              marginTop: '2px', minWidth: '100px',
            }}>
              <div style={{ fontSize: '10px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>
                Visible questions
              </div>
              {[3, 4, 5, 6, 7, 8].map(n => (
                <label key={n} style={{
                  display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer',
                  padding: '2px 0', fontSize: '10px', color: '#555',
                }}>
                  <input
                    type="radio" name="top10count" checked={visibleCount === n}
                    onChange={() => handleVisibleCountChange(n)}
                    style={{ margin: 0, width: '12px', height: '12px' }}
                  />
                  {n} questions
                </label>
              ))}
            </div>
          )}
          {usedQuestions.length > 0 && (
            <button
              onClick={clearHistory}
              style={{
                background: 'none', border: '1px solid #e0e0e0', borderRadius: '4px',
                padding: '1px 6px', fontSize: '10px', cursor: 'pointer', color: '#999',
              }}
              title={`Clear ${usedQuestions.length} recycled questions`}
              aria-label="Clear recycled question history"
            >
              🗑️
            </button>
          )}
          <button
            onClick={fetchQuestions}
            disabled={loading}
            style={{
              background: 'none', border: '1px solid #e0e0e0', borderRadius: '4px',
              padding: '1px 6px', fontSize: '10px', cursor: 'pointer', color: '#666',
            }}
            title="Refresh — generates new questions (excludes recycled ones)"
            aria-label="Refresh top 10 questions"
          >
            {loading ? '⏳' : '🔄'}
          </button>
        </span>
      </div>
      <CIDTooltip
        clientId={clientId}
        visible={cidTooltipVisible}
        onDismiss={() => setCidTooltipVisible(false)}
        anchorRef={top10HeaderRef}
        productType={productType}
      />

      {/* Scrollable questions container — fixed height based on visibleCount */}
      <div style={{
        maxHeight: loading ? 'auto' : `${scrollHeight}px`,
        overflowY: 'auto', overflowX: 'hidden',
      }}>
        {/* Loading state */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '12px', color: '#aaa', fontSize: '11px' }}>
            🧠 Generating questions...
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div style={{
            padding: '8px', background: '#fff3e0', borderRadius: '4px',
            fontSize: '11px', color: '#e65100', marginBottom: '4px',
          }}>
            ⚠️ {error}
            <button onClick={fetchQuestions} style={{
              marginLeft: '6px', background: 'none', border: 'none',
              color: '#1565c0', cursor: 'pointer', fontSize: '11px', textDecoration: 'underline',
            }}>Retry</button>
          </div>
        )}

        {/* Questions list */}
        {!loading && questions.map((item, i) => {
          const actionStyle = ACTION_COLORS[item.action_type] || ACTION_COLORS['Monitor Outcome'];
          return (
            <div
              key={`${item.q}-${i}`}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px', width: '100%',
                padding: '6px 8px', background: i % 2 === 0 ? '#fafafa' : '#fff',
                border: '1px solid #f0f0f0', borderRadius: '5px', marginBottom: '2px',
                minHeight: '32px',
              }}
            >
              <span style={{ fontSize: '12px', flexShrink: 0 }}>{item.icon}</span>
              <button
                onClick={() => onRunQuestion(item.q)}
                style={{
                  flex: 1, background: 'none', border: 'none', cursor: 'pointer',
                  textAlign: 'left', fontSize: '11px', color: '#333', padding: 0,
                  lineHeight: 1.3,
                }}
                title="Click to run this question"
              >
                {item.q}
              </button>
              <span style={{
                fontSize: '8px', padding: '1px 4px', borderRadius: '3px',
                background: actionStyle.bg, color: actionStyle.color,
                fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
              }}>
                {actionStyle.label}
              </span>
              {onAddToCustom && (
                <button onClick={() => handleAddToCustom(item)} style={{ ...iconBtnStyle, fontSize: '12px' }}
                  title="Add to Custom Intelligence" aria-label={`Add question ${i + 1} to Custom Intelligence`}>➕</button>
              )}
              <button onClick={() => handleShareClick(item)} style={{ ...iconBtnStyle, fontSize: '12px' }}
                title="Share this question" aria-label={`Share question ${i + 1}`}>📤</button>
              <button onClick={() => handleDeepDiveClick(item)} style={{ ...iconBtnStyle, fontSize: '13px' }}
                title="Take me to separate LLM session" aria-label={`Deep-dive question ${i + 1}`}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1565c0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </button>
              <span onClick={() => onRunQuestion(item.q)}
                style={{ color: '#1565C0', fontSize: '12px', flexShrink: 0, cursor: 'pointer' }}
                title="Run" role="button" tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onRunQuestion(item.q)}
                aria-label={`Run question ${i + 1}`}>→</span>
            </div>
          );
        })}

        {/* Empty state */}
        {!loading && questions.length === 0 && !error && (
          <div style={{
            textAlign: 'center', padding: '12px', color: '#aaa', fontSize: '11px',
            background: '#f8f9fa', borderRadius: '6px',
          }}>
            ✅ All questions consumed.
            <button onClick={fetchQuestions} style={{
              marginLeft: '6px', background: '#1565c0', color: '#fff', border: 'none',
              borderRadius: '4px', padding: '3px 10px', cursor: 'pointer', fontSize: '10px',
            }}>🔄 New</button>
            {usedQuestions.length > 0 && (
              <button onClick={clearHistory} style={{
                marginLeft: '4px', background: 'none', color: '#999', border: '1px solid #ddd',
                borderRadius: '4px', padding: '3px 10px', cursor: 'pointer', fontSize: '10px',
              }}>🗑️ Reset ({usedQuestions.length})</button>
            )}
          </div>
        )}
      </div>

      {/* Consent Modal */}
      {consentType && consentQuestion && (
        <ConsentModal type={consentType} question={consentQuestion}
          onConfirm={handleConsentConfirm} onCancel={handleConsentCancel} />
      )}

      {/* Deep-Dive Session Modal — uses user's selected LLM (llmProviderId), not the blotter's Gemini */}
      {deepDiveQuestion && (
        <DeepDiveModal question={deepDiveQuestion} actionType={deepDiveActionType}
          clientId={clientId} productType={productType}
          llmProvider={llmProviderId || 'gemini'}
          onClose={() => { setDeepDiveQuestion(null); setDeepDiveActionType(null); }} />
      )}
    </div>
  );
};

/**
 * Get the Top-10 BI questions for a product type (legacy sync export).
 * Now returns a placeholder — actual questions are fetched via API.
 * Kept for backward compatibility with any code that imports this.
 */
export function getTop10Questions(productType) {
  return [
    { q: 'Loading AI-generated questions...', icon: '🧠' },
  ];
}

export { DeepDiveModal };

export default LLMTop10Panel;
